import React, { useMemo } from 'react';
import { Checkbox } from '../shadcn/checkbox';
import { Button } from '../shadcn/button';
import { Eye, FileText } from 'lucide-react';
import { getStatusObject } from '../../utils/statuses';
import { formatPhone, formatAiReason } from '../../utils/formatters';
import { getTriggeredReasons } from '../../utils/ruleDisplay';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const PatientRow = ({
  patient,
  isSelected,
  onSelect,
  onClick,
  onMessage,
  formatLastTouch,
}) => {
  const statusInfo = useMemo(() => {
    const obj = getStatusObject(patient.status);
    return { label: obj.label, color: obj.color };
  }, [patient.status]);

  return (
    <div
      onClick={onClick}
      className={`
        group flex items-center gap-4 px-5 py-3 cursor-pointer
        transition-colors duration-100
        ${isSelected ? 'bg-slate-50' : 'hover:bg-slate-50/70'}
        border-b border-slate-100 last:border-b-0
      `}
    >
      {/* Checkbox */}
      <div onClick={(e) => e.stopPropagation()}>
        <Checkbox
          checked={isSelected}
          onCheckedChange={onSelect}
        />
      </div>

      {/* Patient Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-slate-900 truncate">{patient.name}</p>
          {/* Engaged badge */}
          {patient.engagedAt && patient.daysSinceEngagement !== undefined && (
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-100 text-purple-700">
              <Eye className="h-3 w-3" />
              Engaged {patient.daysSinceEngagement === 0 ? 'today' : `${patient.daysSinceEngagement}d ago`}
            </span>
          )}
          {/* Import date badge */}
          {patient.import_run_id && patient.created_at && (
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-500">
              <FileText className="h-3 w-3" />
              Imported {dayjs(patient.created_at).fromNow()}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 mt-0.5">
          {patient.phone && (
            <span className="text-xs text-slate-400">{formatPhone(patient.phone)}</span>
          )}
          {patient.email && (
            <span className="text-xs text-slate-400 truncate">{patient.email}</span>
          )}
        </div>
        {/* Why flagged — prefer primary_flag (human-readable), fall back to ai_reason, then triggered rules */}
        {(() => {
          if (patient.primary_flag) {
            return (
              <p className="text-xs text-slate-400 mt-1 truncate">
                {patient.primary_flag}
              </p>
            );
          }
          if (patient.ai_reason) {
            return (
              <p className="text-xs text-slate-400 mt-1 truncate">
                {formatAiReason(patient.ai_reason)}
              </p>
            );
          }
          const reasons = getTriggeredReasons(patient.triggered_rules);
          if (reasons.length === 0) return null;
          return (
            <p className="text-xs text-slate-400 mt-1">
              {reasons.join(' · ')}
            </p>
          );
        })()}
      </div>

      {/* Risk + Status */}
      <div className="hidden sm:flex items-center gap-1.5">
        {patient.risk && (
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
            patient.risk.toLowerCase() === 'high' ? 'bg-red-100 text-red-700' :
            patient.risk.toLowerCase() === 'medium' ? 'bg-amber-100 text-amber-700' :
            'bg-emerald-100 text-emerald-700'
          }`}>
            {patient.risk}
          </span>
        )}
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusInfo.color}`}>
          {statusInfo.label}
        </span>
      </div>

      {/* Last Contact */}
      <span className="hidden md:block text-xs text-slate-400 w-20 text-right">
        {formatLastTouch(patient.lastTouch, patient)}
      </span>

      {/* Action */}
      <div onClick={(e) => e.stopPropagation()}>
        <Button
          size="sm"
          onClick={onMessage}
          className="opacity-0 group-hover:opacity-100 transition-opacity bg-slate-900 hover:bg-slate-800 text-white text-xs"
        >
          Message
        </Button>
      </div>
    </div>
  );
};

export default PatientRow;
